import { useTelemetry } from '@tbitop/telemetry';
import { defaultTo, isNull } from 'lodash-es';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AuthenticationConfig } from './authentication';
import { ApplicationNotEnabledError, authenticate } from './authentication';
import { log } from './log';
import type { Session, User } from './session';

export type SessionHolder = {
  session: Session | null;
  logout: () => Promise<void>;
};

export const SessionContext = createContext<SessionHolder>({
  session: null,
  logout: async () => undefined,
});

export const useAuthentication = (config: AuthenticationConfig): SessionHolder => {
  const telemetry = useTelemetry();
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    (async () => {
      if (!isNull(session)) {
        return;
      }

      try {
        setSession(await authenticate(config));
      } catch (error) {
        handleAuthenticationError(error);
      }
    })();
  }, [config, session]);

  useEffect(() => {
    if (isNull(session)) {
      return;
    }

    session.onLogout(async () => {
      await session.destroy();
      setSession(null);
    });

  }, [session]);

  useEffect(() => {
    if (isNull(session)) {
      telemetry?.unsetAuthenticatedUser();
      return;
    }

    const handleUserChange = (user: User | null) => {
      if (isNull(user)) {
        telemetry?.unsetAuthenticatedUser();
      } else {
        telemetry?.setAuthenticatedUser(user.email, user.activeOrganization.name);
      }
    };

    const user = session.getUser();
    handleUserChange(user);

    return session?.onUserChange(handleUserChange);
  }, [session, telemetry]);

  const logout = useCallback(
    async () => session?.logout(),
    [session],
  );

  return {
    session,
    logout,
  };
};

export const useSession = () => {
  const { session } = useContext(SessionContext);
  return session;
};

export const useLogout = () => {
  const { logout } = useContext(SessionContext);
  return logout;
};

export const useUser = () => {
  const { session } = useContext(SessionContext);
  return useMemo(() => defaultTo(session?.getUser(), null), [session]);
};

export const useGetAccessToken = () => {
  const { session } = useContext(SessionContext);

  const getAccessToken = useCallback(
    () => defaultTo(session?.getAccessToken(), null),
    [session],
  );

  return getAccessToken;
};

export const useAuthenticated = () => {
  const { session } = useContext(SessionContext);
  const [authenticated, setAuthenticated] = useState<boolean>(
    defaultTo(session?.isAuthenticated, false),
  );

  // The session might have been undefined when the hook was first executed. In that case, the default
  // value would be false. When the session is initialized at a later point, the hook will be called again.
  // At that point, the session.isAuthenticated might already be true before we can set up the
  // AuthenticatedChange event listener. That's why we need to update the initial value when the session
  // is initialized and the hook's internal state does not reflect the session's state.
  useEffect(
    () => {
      if (!isNull(session) && authenticated !== session.isAuthenticated) {
        setAuthenticated(session.isAuthenticated);
      }
    },
    [session, authenticated],
  );

  useEffect(
    () => {
      session?.onAuthenticatedChange(authenticated => setAuthenticated(authenticated));
    },
    [session],
  );

  return authenticated;
};

function handleAuthenticationError(error: unknown) {
  if (error instanceof ApplicationNotEnabledError) {
    log.warn(error.message);
    window.location.replace(window.location.origin);
    return;
  }
  log.error(error);
}
